import { createColumnHelper } from '@tanstack/react-table';

import TableColumnHeader from '../TableColumnHeader';

import type { ReactNode } from 'react';
import type { BaseColumnOptions, ColumnFactory } from './types';

/**
 * Options for display columns
 */
export interface DisplayColumnOptions extends BaseColumnOptions {
  /**
   * Enable sorting for this column
   */
  sortable?: boolean;
  /**
   * Tooltip to display next to the column header
   */
  infoTooltip?: string;
}

/**
 * Factory function to create a display column (no data accessor)
 *
 * Display columns are useful for action buttons, custom controls,
 * or any content that doesn't map to a specific data property.
 */
export function createDisplayColumn<TData>(config: {
  id: string;
  label: string;
  options?: DisplayColumnOptions;
  cell: (row: TData) => ReactNode;
// TValue is unknown (not never) because columnHelper.display() returns ColumnDef<TData, unknown>
}): ColumnFactory<TData> {
  const { id, label, options = {}, cell } = config;
  const { size = 150, sortable = false, className, infoTooltip, meta } = options;

  const columnHelper = createColumnHelper<TData>();

  return columnHelper.display({
    id,
    size,
    header: ({ table }) =>
      sortable ? (
        <TableColumnHeader
          className={className}
          accessor={id}
          infoTooltip={infoTooltip}
          label={label}
          setSorting={table.setSorting}
          sortable
          tableState={table.getState()}
        />
      ) : (
        <TableColumnHeader
          className={className}
          accessor={id}
          infoTooltip={infoTooltip}
          label={label}
          tableState={table.getState()}
        />
      ),
    cell: (info) => cell(info.row.original),
    meta,
  });
}
